import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Spin,
  Space,
  Row,
  Col,
  Badge,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  TagOutlined,
  GlobalOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Option } = Select;

const EditTag = () => {
  const { tag_id } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [multilingualData, setMultilingualData] = useState([]);

  const fetchTagDetails = useCallback(async () => {
    if (!tag_id) {
      notify.error({ message: "Tag ID is required" });
      navigate("/tags");
      return;
    }

    setLoadingData(true);
    try {
      const response = await masterService.getSingleTag(tag_id);

      if (response.data && response.data.status) {
        const tagData = response.data.data?.[0] || response.data.data;
        if (tagData && tagData.tag_name) {
          form.setFieldsValue({
            tag_name: tagData.tag_name,
            tag_is_active: tagData.tag_is_active || "Y",
            tag_description: tagData.tag_description || "",
            tag_keywords: tagData.tag_keywords
              ? String(tagData.tag_keywords)
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [],
          });
          if (Array.isArray(tagData.multilingual_data)) {
            setMultilingualData(
              tagData.multilingual_data.map((d) => ({
                lang_id: d.lang_id ?? null,
                tag_name: d.tag_name ?? "",
                tag_description: d.tag_description ?? "",
                tag_keywords: d.tag_keywords
                  ? String(d.tag_keywords)
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : [],
              }))
            );
          }
        } else {
          notify.error({ message: "Tag data not found" });
          navigate("/tags");
        }
      } else {
        notify.error({
          message: response.data?.message || "Failed to fetch tag details",
        });
        navigate("/tags");
      }
    } catch (error) {
      console.error("Error fetching tag details:", error);
      notify.error({ message: "Error fetching tag details" });
      navigate("/tags");
    } finally {
      setLoadingData(false);
    }
  }, [tag_id, form, navigate]);

  useEffect(() => {
    setPageTitle("Edit Tag");
    fetchTagDetails();
  }, [fetchTagDetails, setPageTitle]);

  // Load languages for multilingual
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const params = {
          perPage: 1000,
          currentPage: 1,
          searchText: "",
          status: "Y",
        };
        const res = await masterService.getVideoLanguages(params);
        const list =
          res?.data?.data?.data || res?.data?.data || res?.data || [];
        setLanguages(list);
      } catch (e) {
        notify.error({ message: "Failed to load languages" });
      }
    };
    loadLanguages();
  }, []);

  const handleSubmit = async (values) => {
    // Validate multilingual data
    const langs = multilingualData.map((d) => d.lang_id).filter(Boolean);
    if (new Set(langs).size !== langs.length) {
      notify.error({ message: "Duplicate languages in multilingual data" });
      return;
    }
    for (let i = 0; i < multilingualData.length; i++) {
      const d = multilingualData[i];
      if (d.lang_id && !d.tag_name) {
        notify.error({
          message: `Language entry #${
            i + 1
          }: Tag name is required when language is selected`,
        });
        return;
      }
    }
    setLoading(true);
    try {
      const parentKeywords = Array.isArray(values.tag_keywords)
        ? values.tag_keywords
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(",")
        : values.tag_keywords || "";
      const multilingualSerialized = (multilingualData || []).map((d) => ({
        ...d,
        tag_keywords: Array.isArray(d.tag_keywords)
          ? d.tag_keywords
              .map((s) => String(s).trim())
              .filter(Boolean)
              .join(",")
          : d.tag_keywords || "",
      }));
      const payload = {
        ...values,
        tag_id: tag_id,
        tag_keywords: parentKeywords,
        multilingual_data: multilingualSerialized,
      };

      const response = await masterService.updateTag(tag_id, payload);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Tag updated successfully",
        });
        navigate("/tags");
      } else {
        notify.error({
          message: response.data.message || "Failed to update tag",
        });
      }
    } catch (error) {
      console.error("Error updating tag:", error);
      notify.error({ message: error?.data?.message || "Error updating tag" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/tags");
  };

  return (
    <div className="min-h-screen">
      <Spin spinning={loadingData}>
        <Card
        >
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleSubmit}
            className="space-y-6"
          >
            {/* Basic Information Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <TagOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Tag Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Tag Name
                      </span>
                    }
                    name="tag_name"
                    rules={[
                      { required: true, message: "Please enter tag name" },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter tag name"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="tag_is_active"
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Description
                      </span>
                    }
                    name="tag_description"
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder="Enter tag description"
                      className="rounded-lg"
                      showCount
                      maxLength={500}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Keywords
                      </span>
                    }
                    name="tag_keywords"
                  >
                    <Select
                      mode="tags"
                      placeholder="Type a keyword and press Enter"
                      tokenSeparators={[","]}
                      open={false}
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Multilingual Data Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <GlobalOutlined />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Multilingual Data
                  </Title>
                </div>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setMultilingualData([
                      ...multilingualData,
                      {
                        lang_id: null,
                        tag_name: "",
                        tag_description: "",
                        tag_keywords: [],
                      },
                    ])
                  }
                  className="rounded-lg"
                >
                  Add Language
                </Button>
              </div>

              {multilingualData.length === 0 && (
                <div
                  className="text-center p-6 rounded-lg"
                  style={{
                    background: "#fafafa",
                    border: "1px dashed #d9d9d9",
                  }}
                >
                  <Text type="secondary" className="text-base">
                    No multilingual data added yet. Click "Add Language" to get
                    started.
                  </Text>
                </div>
              )}

              <div className="space-y-4">
                {multilingualData.map((data, index) => (
                  <Card
                    key={`ml-${index}`}
                    className="rounded-lg"
                    title={
                      <div className="flex items-center gap-2">
                        <Text strong className="text-base">
                          Language Entry #{index + 1}
                        </Text>
                        {data.lang_id && (
                          <Badge
                            count={
                              languages.find((l) => l.lang_id === data.lang_id)
                                ?.lang_name || "Unknown Language"
                            }
                            style={{ backgroundColor: "#1890ff" }}
                          />
                        )}
                      </div>
                    }
                    extra={
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          setMultilingualData(
                            multilingualData.filter((_, i) => i !== index)
                          )
                        }
                      >
                        Remove
                      </Button>
                    }
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={
                            <span
                              className="font-semibold"
                              style={{ color: "rgba(0, 0, 0, 0.88)" }}
                            >
                              Language
                            </span>
                          }
                          required
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            value={data.lang_id}
                            placeholder="Select language"
                            onChange={(value) => {
                              const arr = [...multilingualData];
                              arr[index].lang_id = value;
                              setMultilingualData(arr);
                            }}
                            showSearch
                            className="rounded-lg"
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {languages.map((lang) => (
                              <Option key={lang.lang_id} value={lang.lang_id}>
                                {lang.lang_name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={
                            <span
                              className="font-semibold"
                              style={{ color: "rgba(0, 0, 0, 0.88)" }}
                            >
                              Tag Name
                            </span>
                          }
                          required
                          style={{ marginBottom: 0 }}
                          rules={[{ validator: noWhitespaceValidator }]}
                        >
                          <Input
                            value={data.tag_name}
                            placeholder="Enter tag name in this language"
                            className="rounded-lg"
                            onChange={(e) => {
                              const arr = [...multilingualData];
                              arr[index].tag_name = e.target.value;
                              setMultilingualData(arr);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]} className="mt-4">
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={
                            <span
                              className="font-semibold"
                              style={{ color: "rgba(0, 0, 0, 0.88)" }}
                            >
                              Description
                            </span>
                          }
                          style={{ marginBottom: 0 }}
                          rules={[{ validator: noWhitespaceValidator }]}
                        >
                          <Input.TextArea
                            rows={3}
                            value={data.tag_description}
                            placeholder="Enter description in this language"
                            className="rounded-lg"
                            onChange={(e) => {
                              const arr = [...multilingualData];
                              arr[index].tag_description = e.target.value;
                              setMultilingualData(arr);
                            }}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={
                            <span
                              className="font-semibold"
                              style={{ color: "rgba(0, 0, 0, 0.88)" }}
                            >
                              Keywords
                            </span>
                          }
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            mode="tags"
                            value={
                              Array.isArray(data.tag_keywords)
                                ? data.tag_keywords
                                : data.tag_keywords
                                ? String(data.tag_keywords)
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                                : []
                            }
                            placeholder="Type a keyword and press Enter"
                            onChange={(value) => {
                              const arr = [...multilingualData];
                              arr[index].tag_keywords = value;
                              setMultilingualData(arr);
                            }}
                            tokenSeparators={[","]}
                            open={false}
                            className="rounded-lg"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div
              className="flex justify-end gap-4 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space size="middle">
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleGoBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {loading ? "Updating..." : "Update Tag"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default EditTag;
